
import { defineComponent } from 'vue'
import { useStore } from '@/store/mfa'
import AuthService from '@/services'
import PageTitle from '@/components/PageTitle.vue'
import PageAction from '@/components/PageAction.vue'
import TextField from '@/components/TextField.vue'
import AlertBanner from '@/components/AlertBanner.vue'
import { UI_DELAY } from '@/constants'
import { AxiosError } from 'axios'
import { AlphaButtonAv } from '@op-frontend/vue2tifywind'

const delay = () => new Promise((resolve) => setTimeout(resolve, UI_DELAY))

export default defineComponent({
	components: {
		PageTitle,
		PageAction,
		TextField,
		AlphaButtonAv,
		AlertBanner
	},
	props: {
		isWebComponent: {
			type: Boolean,
			default: false
		},
		redirectUrl: {
			type: String,
			default: ''
		},
		showExpiredMessage: {
			type: String,
			default: null
		},
		lang: {
			type: String,
			default: 'id'
		}
	},
	setup() {
		const store = useStore()
		return { store }
	},
	data() {
		return {
			attempt: 0,
			form: {
				mfa: '',
				redirectUrl: ''
			},
			errors: {
				mfa: ''
			},
			apiErrorMessage: ''
		}
	},
	computed: {
		isApiError(): boolean {
			return !!this.apiErrorMessage
		},
		isErrorMfa(): boolean {
			return !!this.errors.mfa
		},
		isRendered(): boolean {
			return this.store.reRender
		}
	},
	watch: {
		'$i18n.locale': {
			async handler() {
				this.store.isRendered(false)
				await delay()
				this.$forceUpdate()
				this.store.isRendered(true)
			},
			immediate: true,
			deep: true
		}
	},

	mounted() {
		const query = new URLSearchParams(window.location.search)
		const redirectUrl = query.get('redirectUrl')
		if (redirectUrl) this.store.setRedirectUrl(redirectUrl)
	},

	methods: {
		setMfa(val: string) {
			this.form.mfa = val
			this.validateForm()
		},

		async resendMFAAction() {
			try {
				await this.store.resend()
			} catch (err) {
				this.handleError(err)
			}
		},

		validateForm() {
			this.apiErrorMessage = ''

			if (!this.form.mfa.length) this.errors.mfa = 'errors.mfa__required'
			else if (!/^\d{6}$/.test(this.form.mfa)) this.errors.mfa = 'errors.mfa__invalid'
			else this.errors.mfa = ''
		},

		handleError(err: AxiosError) {
			const data = err.response?.data as { errorCode: string }
			const errorCode = data?.errorCode
			if (errorCode === 'MFA_EXPIRED') {
				this.apiErrorMessage = 'errors.mfa__expired'
			} else if (errorCode === 'MFA_INVALID') {
				this.apiErrorMessage = 'errors.mfa__invalid'
			} else if (errorCode === 'MFA_FAILED') {
				this.apiErrorMessage = 'errors.mfa__failed'
			} else if (errorCode === 'MFA_LIMIT_REACHED') {
				this.apiErrorMessage = 'errors.mfa__limit_reached'
			} else {
				this.apiErrorMessage = 'errors.mfa__general'
			}
		},

		async mfaAction() {
			this.validateForm()
			this.attempt = 0

			this.form.redirectUrl = this.store.redirectUrl
				? this.store.redirectUrl
				: this.redirectUrl

			if (!this.isErrorMfa && !this.isApiError) {
				try {
					const { redirectUrl } = await AuthService.validateMfaAction(this.form)
					window.location.href = redirectUrl
				} catch (err) {
					this.handleError(err)
				}
			}
		},

		resetErrorMessage() {
			this.apiErrorMessage = ''
		}
	}
})
